import { z } from 'zod';
import { firingAlertSchema } from './alerts.js';
import { presenceSchema, publicUserSchema } from './user.js';
import { messageSchema, reactionSummarySchema } from './message.js';
import { notificationSchema } from './notification.js';
import { channelWithMembershipSchema } from './channel.js';
import { canvasSchema } from './canvas.js';
import { listSchema } from './list.js';
import { huddleSchema } from './huddle.js';
/**
 * A valos ideju reteg szerzodese.
 *
 * Iranyelv (docs/03-architecture.md 4. fejezet):
 *   - Az IRAS mindig REST-en megy (idempotens, ujraprobalhato, hibakezelheto).
 *   - A WebSocket CSAK lefele szallit esemenyt.
 *   - Minden uzenet-esemeny hordozza a csatorna monoton `seq` erteket. Ha a kliens
 *     hezagot lat, REST-tel potol (`?afterSeq=`). Ettol nem lehet nema uzenetvesztes:
 *     nem kell megbizni abban, hogy a socket mindig kezbesitett.
 */
export const WS_PROTOCOL_VERSION = 1;
// ─── Kliens -> szerver ────────────────────────────────────────────────────────
export const clientFrameSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('ping'),
        t: z.number(),
        /**
         * Hatterben van-e AZ AZ ablak, ahonnan az eleljel jott.
         *
         * Ebbol tudja a szerver, hogy erdemes-e push-t kuldeni: a nyitott, de
         * elrejtett ablak nem tud rendszer-ertesitest mutatni (a bongeszo
         * felfuggeszti), tehat ugyanugy push kell, mintha be lenne zarva.
         *
         * ELHAGYHATO: a regebbi kliensek nem kuldik, es ok is elorben levonek
         * szamitanak - ez a regi viselkedes.
         */
        hidden: z.boolean().optional(),
        /**
         * Ennek az eszkoznek a push-feliratkozasa, ha van.
         *
         * Ebbol tudja a szerver, MELYIK eszkoz latszik: a push eszkozonkent megy,
         * es csak arra az eszkozre nem kell, amelyiken az ablak eppen elorben
         * van. A gepen nyitott ablak nem nemithatja el a zsebben levo telefont.
         *
         * ELHAGYHATO: feliratkozas nelkuli ablak (desktop app, push nelkuli
         * bongeszo) egyetlen push-t sem nemit el.
         */
        pushEndpoint: z.string().url().max(2048).optional(),
    }),
    z.object({
        type: z.literal('typing'),
        channelId: z.string(),
        parentId: z.string().nullable().optional(),
    }),
    z.object({ type: z.literal('presence'), presence: presenceSchema }),
]);
// ─── Szerver -> kliens ────────────────────────────────────────────────────────
export const serverFrameSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('hello'),
        protocolVersion: z.number(),
        userId: z.string(),
        connectionId: z.string(),
        serverTime: z.number(),
        heartbeatIntervalMs: z.number(),
    }),
    z.object({ type: z.literal('pong'), t: z.number() }),
    /**
     * A munkamenetet visszavontak (tavoli kileptetes). A szerver a keret utan
     * 4401-gyel bontja a kapcsolatot; a `null` a felhasznalo MINDEN munkamenetet
     * jelenti. A tobbi eszkoz ugyanezt a keretet kapja, de nala nem bont.
     */
    z.object({ type: z.literal('session.revoked'), sessionId: z.string().nullable() }),
    z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
    z.object({ type: z.literal('message.created'), message: messageSchema }),
    /**
     * Szerkesztes, torles es reakcio NEM kap uj sorszamot - ezek meglevo uzenetet
     * modositanak, azonosito alapjan. Ha egy ilyen esemeny elveszik, a kovetkezo
     * elozmeny-lekeres (hezag-potlas vagy csatornavaltas) helyreallitja az allapotot.
     */
    z.object({ type: z.literal('message.updated'), message: messageSchema }),
    z.object({
        type: z.literal('message.deleted'),
        channelId: z.string(),
        messageId: z.string(),
        parentId: z.string().nullable(),
    }),
    z.object({
        type: z.literal('reaction.changed'),
        channelId: z.string(),
        messageId: z.string(),
        reactions: z.array(reactionSummarySchema),
    }),
    /**
     * Ertesites - CSAK a cimzett sajat temajara megy.
     * A dontest (kinek szoljon) a szerver hozza; a kliens csak megjeleniti.
     */
    z.object({ type: z.literal('notification'), notification: notificationSchema }),
    /** Kituzes valtozott - a csatorna minden tagjat erinti. */
    z.object({
        type: z.literal('pin.changed'),
        channelId: z.string(),
        messageId: z.string(),
        isPinned: z.boolean(),
    }),
    /**
     * FONTOS jeloles valtozott - mindenkit erint.
     *
     * Ugyanugy kozos dontes, mint a kituzes: barki teheti es barki veheti le,
     * es ha nem menne ki mindenkire, ket fulon ket kulonbozo szinu uzenet
     * allna ugyanarrol.
     */
    z.object({
        type: z.literal('important.changed'),
        channelId: z.string(),
        messageId: z.string(),
        isImportant: z.boolean(),
    }),
    /** FELADAT-jeloles vagy kipipalas valtozott - a csatorna minden tagja latja. */
    z.object({
        type: z.literal('task.changed'),
        channelId: z.string(),
        messageId: z.string(),
        task: z
            .object({ done: z.boolean(), doneBy: z.string().nullable(), doneAt: z.string().nullable() })
            .nullable(),
    }),
    /** Szavazat erkezett vagy lezartak egy szavazast (U4) - a kliens ujrakerdezi. */
    z.object({
        type: z.literal('poll.updated'),
        channelId: z.string(),
        pollId: z.string(),
    }),
    /** Mentes valtozott - CSAK a sajat fulekre megy (a mentes szemelyes). */
    z.object({
        type: z.literal('saved.changed'),
        channelId: z.string(),
        messageId: z.string(),
        isSaved: z.boolean(),
    }),
    /** A szal-szulo valaszszamlaloja valtozott. */
    z.object({
        type: z.literal('thread.updated'),
        channelId: z.string(),
        parentId: z.string(),
        replyCount: z.number(),
        lastReplyAt: z.string().nullable(),
    }),
    /**
     * A canvas ADATLAPJA valtozott: uj canvas, atnevezes, kiemeles, torles.
     *
     * A canvas TARTALMA nem ezen jon at - az sajat binaris WebSocketen megy
     * (`/ws/canvas`), mert karakterenkenti frissiteseket szallit, es azokra a
     * csatorna minden tagjanak feliratkoztatasa feleslegesen szorna szet
     * forgalmat. Ide csak az a valtozas kerul, ami a LISTAN latszik.
     */
    z.object({ type: z.literal('canvas.changed'), canvas: canvasSchema }),
    z.object({
        type: z.literal('canvas.removed'),
        channelId: z.string(),
        canvasId: z.string(),
    }),
    /** A csatorna canvasainak sorrendje valtozott (huzas) - minden tagnal ugyanaz. */
    z.object({
        type: z.literal('canvas.reordered'),
        channelId: z.string(),
        canvasIds: z.array(z.string()),
    }),
    /**
     * Egy MEGOSZTOTT szemelyes canvas valtozott (uj/visszavont megosztas,
     * atnevezes, torles). A cimzettek listai tobb helyen lehetnek (sajat
     * jegyzetek, csatornak), ezert csak jelzes megy: a kliens ujratolti oket.
     * A `sharedBy` csak uj megosztasnal van kitoltve - ilyenkor szolunk is.
     */
    z.object({
        type: z.literal('canvas.shared'),
        canvasId: z.string(),
        title: z.string().optional(),
        sharedBy: z.string().optional(),
    }),
    /**
     * LISTA valtozott - a teteleivel egyutt.
     *
     * A canvasszal ellentetben itt a TELJES lista megy at, a tetelekkel.
     * A canvas tartalma sajat, binaris csatornan utazik (Yjs), mert egy
     * dokumentum barmekkora lehet; egy lista viszont nehany tucat sor, es
     * egyben atkuldve nem kell kulon lekerdezes minden pipalas utan.
     */
    z.object({ type: z.literal('list.changed'), list: listSchema }),
    z.object({
        type: z.literal('list.removed'),
        channelId: z.string(),
        listId: z.string(),
    }),
    /**
     * EMLEKEZTETO jart le - a cimzettnek szol.
     *
     * Ez az egyetlen esemeny, amit a felulet a nemitastol FUGGETLENUL megmutat:
     * felugro ablakban, hangjelzessel. Aki emlekeztetot ker, azt eppen az
     * erdekli, hogy akkor is eszrevegye, ha epp csendre allitotta magat.
     */
    z.object({
        type: z.literal('reminder.due'),
        reminderId: z.string(),
        /** A KIKULDES azonositoja - a felugro ablakon ezzel megy a kipipalas. */
        deliveryId: z.string(),
        text: z.string(),
        creatorId: z.string(),
        remindAt: z.string(),
        /** Ha egy uzenetre szol (mentett elem): a felugro ablak oda visz. */
        link: z.object({ channelId: z.string(), messageId: z.string() }).optional(),
    }),
    /**
     * NAPTAR valtozott (H1) - a felulet ujrakeri a naptarat. A tartalom nem
     * utazik itt: a lekeres a jogosultsagot is ujra ellenorzi.
     */
    z.object({ type: z.literal('calendar.changed') }),
    /**
     * Naptarbejegyzes KEZDODIK (H1) - a tagoknak szol, a beallitott idovel
     * elotte. Hivasnal a felulet "Csatlakozas" gombot mutat.
     */
    z.object({
        type: z.literal('calendar.reminder'),
        eventId: z.string(),
        title: z.string(),
        startsAt: z.string(),
        withCall: z.boolean(),
        channelId: z.string().nullable(),
        scope: z.enum(['channel', 'group']),
        groupId: z.string().nullable(),
    }),
    /**
     * RIASZTAS a Prometheusbol - csak a szuperadminoknak megy.
     *
     * Ugyanaz az ut, mint az emlekezteto: felugro ablak, hang, push. Aki
     * uzemelteti a rendszert, ugyanabban a rendszerben tudja meg, ha baj van.
     */
    z.object({
        type: z.literal('alert.fired'),
        alert: firingAlertSchema,
        grafanaUrl: z.string().nullable(),
    }),
    z.object({ type: z.literal('alert.resolved'), fingerprint: z.string() }),
    z.object({ type: z.literal('channel.created'), channel: channelWithMembershipSchema }),
    z.object({ type: z.literal('channel.updated'), channel: channelWithMembershipSchema }),
    z.object({ type: z.literal('channel.removed'), channelId: z.string() }),
    /**
     * Egy telefonvonal megvaltozott (uj, atiranyitott, torolt).
     *
     * Tartalom nelkul: mindenki ujrakeri a SAJAT vonalait. Enelkul a kliens a
     * regi beallitast mutatta - az oldalsav es a tarcsazo csak egy kesobbi
     * frissitesnel (tipikusan epp a kovetkezo hivas kozben) ugrott at.
     */
    z.object({ type: z.literal('phone.lines.changed') }),
    z.object({
        type: z.literal('member.joined'),
        channelId: z.string(),
        userId: z.string(),
        memberCount: z.number(),
    }),
    z.object({
        type: z.literal('member.left'),
        channelId: z.string(),
        userId: z.string(),
        memberCount: z.number(),
    }),
    /**
     * Valaki elolvasta a csatorna uzeneteit - a TOBBIEKNEK szol.
     *
     * A `read.updated` a sajat fuleinknek megy; ez a masik irany: ebbol tudja a
     * kuldo, hogy latta-e valaki az uzenetet. Csak a POZICIOT kuldjuk
     * (`lastReadSeq`), nem uzenet-azonositokat - abbol a kliens maga eldonti,
     * melyik uzenetig jutott az illeto.
     */
    z.object({
        type: z.literal('read.receipt'),
        channelId: z.string(),
        userId: z.string(),
        lastReadSeq: z.number(),
    }),
    /** Sajat olvasottsag valtozott - ettol marad szinkronban tobb megnyitott ful. */
    z.object({
        type: z.literal('read.updated'),
        channelId: z.string(),
        lastReadSeq: z.number(),
    }),
    /**
     * Huddle-esemenyek.
     *
     * A resztvevo-lista mindig TELJESEN jon, nem kulonbsegkent. Egy elveszett
     * esemeny igy nem tud tartos elteres lenni: a kovetkezo esemeny helyreallitja
     * a kepet. Ez a "nem latszik, ki van bent" hiba elleni vedelem.
     */
    z.object({ type: z.literal('huddle.started'), huddle: huddleSchema }),
    z.object({ type: z.literal('huddle.updated'), huddle: huddleSchema }),
    z.object({
        type: z.literal('huddle.ended'),
        channelId: z.string(),
        huddleId: z.string(),
    }),
    /**
     * CSAK a sajat fulekre: behivtak egy huddle-be.
     *
     * A behivott mar latszik a resztvevok kozott `invited` allapotban; ez az
     * esemeny az, ami nala csengetni fog.
     */
    z.object({
        type: z.literal('huddle.invited'),
        huddle: huddleSchema,
        invitedByName: z.string(),
        channelName: z.string().nullable(),
        /**
         * Ment-e rola push is (a bejovo telefonhivasnal igen).
         *
         * Ha igen, a push-ra feliratkozott eszkoz NEM mutat melle sajat
         * rendszer-ertesitest - kulonben ugyanaz a hivas ketszer jelenne meg.
         */
        pushed: z.boolean().optional(),
    }),
    /**
     * CSAK a sajat fulekre: a behivast VISSZAVONTAK, mielott felvettuk volna.
     *
     * A hivas maga tovabb el (a hivo bent marad masokkal), csak NALUNK szunt meg - a
     * csengest le kell allitani, a hivast el kell tuntetni. A huddle.ended ezt nem
     * fedi: az csak akkor jon, ha az egesz hivas veget ert.
     */
    z.object({
        type: z.literal('huddle.invite_cancelled'),
        huddleId: z.string(),
        channelId: z.string(),
    }),
    /**
     * CSAK a sajat fulekre: a szerver kileptetett a huddle-bol.
     * Tipikusan azert, mert egy masik fulon vettuk at a hivast.
     */
    z.object({
        type: z.literal('huddle.evicted'),
        huddleId: z.string(),
        reason: z.string(),
    }),
    /**
     * Efemer valasz - CSAK a kero sajat fuleire megy, es nem kerul az elozmenybe.
     *
     * A slash parancs igy tud olyat valaszolni, ami csak a kerdezot erdekli
     * ("nincs ilyen allat"), anelkul hogy a csatornat teleszemetelne.
     */
    z.object({
        type: z.literal('ephemeral'),
        channelId: z.string(),
        text: z.string(),
        source: z.string(),
    }),
    z.object({ type: z.literal('presence.changed'), userId: z.string(), presence: presenceSchema }),
    /** Profil vagy statusz valtozott - a nev es a statusz a folyamban is latszik. */
    z.object({ type: z.literal('user.updated'), user: publicUserSchema }),
    z.object({
        type: z.literal('typing.started'),
        channelId: z.string(),
        parentId: z.string().nullable(),
        userId: z.string(),
        expiresAt: z.number(),
    }),
]);
/** Redis pub/sub csatornanevek - egy helyen, hogy ne csusszanak el. */
export const topics = {
    channel: (channelId) => `ch:${channelId}`,
    user: (userId) => `usr:${userId}`,
    workspace: () => 'ws:áll',
};
export const TOPIC_PATTERNS = ['ch:*', 'usr:*', 'ws:áll'];
export const HEARTBEAT_INTERVAL_MS = 15_000;
export const PRESENCE_TTL_SECONDS = 45;
/**
 * Meddig szamit elorben levonek egy ablak az utolso lathato eleljele utan.
 *
 * Halo, nem a fo mechanizmus: elrejteskor a kliens AZONNAL szol, ezert a
 * push tipikusan a kovetkezo pillanatban indul, nem itt. Ez arra az esetre
 * kell, amikor egy ablak ugy tunik el, hogy nem tudott elkoszonni (osszeomlas,
 * halozat-szakadas) - ilyenkor legkesobb ennyi ido mulva push-ra valtunk.
 */
export const FOREGROUND_TTL_SECONDS = 45;
export const TYPING_TTL_SECONDS = 5;
//# sourceMappingURL=ws.js.map